import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileSignature, Clock, FileText } from "lucide-react";
import { api, errMsg, fmtDate, fmtDateTime, API, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function DeregisterDialog({ row, onClose, onDone }) {
  const [letter, setLetter] = useState(null);
  const [reason, setReason] = useState("");
  useEffect(() => { api.post(`/cases/${row.case_id}/intake/deregister/draft`).then((r) => setLetter(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); }); }, [row.case_id]); // eslint-disable-line
  const send = async () => {
    try {
      await api.put(`/cases/${row.case_id}/intake/deregister/draft`, { subject: letter.subject, body: letter.body });
      await api.post(`/cases/${row.case_id}/intake/deregister/send`, { reason });
      toast.success("Notice signed and sent to all parties — application deregistered"); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="deregister-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSignature className="h-4 w-4" />Review & sign notice of deregistration · {row.case_number}</DialogTitle></DialogHeader>
        {!letter ? <div className="text-sm text-slate-500">Generating letter from the lodgement timeline…</div> : (
          <div className="space-y-3">
            <div className="text-[11px] text-slate-500">{letter.basis}. Edit anything before signing. Sending closes the proceeding and notifies every party (portal + email).</div>
            <div><Label>Subject</Label><Input value={letter.subject} onChange={(e) => setLetter({ ...letter, subject: e.target.value })} data-testid="dereg-subject" /></div>
            <div><Label>Letter</Label><Textarea rows={14} className="font-mono text-xs" value={letter.body} onChange={(e) => setLetter({ ...letter, body: e.target.value })} data-testid="dereg-body" /></div>
            <div><Label>Member's written reasons (audited, 10+ characters)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. No response to notice or follow-up; no extension sought" data-testid="dereg-reason" /></div>
          </div>)}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dereg-cancel">Cancel</Button>
          <Button className="bg-rose-700 hover:bg-rose-800" disabled={!letter || reason.trim().length < 10} onClick={send} data-testid="dereg-sign-send"><FileSignature className="h-4 w-4 mr-1" />Sign & send to parties</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContinueDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({ days: 7, reason: "" });
  const go = async () => { try { await api.post(`/cases/${row.case_id}/intake/directions/continue`, { days: Number(f.days), reason: f.reason }); toast.success(`Final ${f.days} day(s) directed — respondent notified`); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="continue-dialog">
        <DialogHeader><DialogTitle>Allow a final period to comply · {row.case_number}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-[100px_1fr] gap-3">
          <div><Label>Days</Label><Input type="number" min="1" max="60" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} data-testid="continue-days" /></div>
          <div><Label>Written reasons (10+ characters)</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="continue-reason" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-blue-900" disabled={f.reason.trim().length < 10} onClick={go} data-testid="continue-submit">Direct final period</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NonComplianceTable({ onChanged }) {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/intake/directions-queue").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  if (rows === null) return null;
  const done = () => { setDlg(null); load(); onChanged && onChanged(); };
  return (
    <Panel title={<span className="flex items-center gap-2"><Clock className="h-4 w-4 text-rose-700" />Awaiting Member directions — respondent non-compliance · {rows.length}</span>} testId="nc-directions-panel">
      <p className="text-xs text-slate-500 mb-3">Notice and follow-up reminder went unanswered. Oldest deadline first. Deregistering generates a signed notice for your review before it is sent.</p>
      {rows.length === 0 ? <Empty text="No matters awaiting directions" /> : (
        <div className="overflow-x-auto -mx-5 -mb-5"><table className="tbl" data-testid="nc-directions-table">
          <thead><tr><th>Case</th><th>Respondent</th><th>Due</th><th>Days past due</th><th>Notice</th><th>Follow-up</th><th>Referred</th><th>Outstanding</th><th></th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.case_id} data-testid={`nc-row-${r.case_id}`}>
              <td><Link to={`/cases/${r.case_id}?tab=lodgement`} className="mono font-semibold text-blue-900 hover:underline" data-testid={`nc-case-${r.case_id}`}>{r.case_number}</Link><div className="text-xs text-slate-500">{r.title}</div></td>
              <td className="text-sm">{r.respondent}</td>
              <td className="text-sm">{fmtDate(r.respondent_due)}</td>
              <td><span className="text-rose-700 font-semibold" data-testid={`nc-overdue-${r.case_id}`}>{r.days_overdue}</span></td>
              <td className="text-xs">{fmtDate(r.nc_notice_at)}</td><td className="text-xs">{fmtDate(r.nc_followup_at)}</td><td className="text-xs">{fmtDateTime(r.flagged_at)}</td>
              <td className="text-xs text-slate-600 max-w-[180px]">{r.missing.join(", ") || "—"}</td>
              <td>{can("approvals.decide") && <div className="flex gap-1.5 justify-end">
                {r.letter_status === "draft" && <a className="text-[11px] underline self-center" href={`${API}/cases/${r.case_id}/intake/deregister/letter.pdf?auth=${localStorage.getItem(TOKEN_KEY)}`} target="_blank" rel="noreferrer" data-testid={`nc-draft-pdf-${r.case_id}`}><FileText className="h-3.5 w-3.5 inline" /> draft</a>}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDlg({ kind: "continue", row: r })} data-testid={`nc-continue-${r.case_id}`}>Allow to continue</Button>
                <Button size="sm" className="h-7 text-xs bg-rose-700 hover:bg-rose-800" onClick={() => setDlg({ kind: "dereg", row: r })} data-testid={`nc-deregister-${r.case_id}`}>Deregister</Button>
              </div>}</td>
            </tr>))}</tbody>
        </table></div>)}
      {dlg?.kind === "dereg" && <DeregisterDialog row={dlg.row} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.kind === "continue" && <ContinueDialog row={dlg.row} onClose={() => setDlg(null)} onDone={done} />}
    </Panel>
  );
}
